// Connection rules for manually drawn canvas edges: one pure table deciding,
// for a (source, target) node pair, which relationship the edge is — or that
// the pair isn't a relationship in the graph model at all. Consumed by
// FlowCanvas twice: React Flow's `isValidConnection` (drag-time gate) and
// `onConnect` (derives the persisted edgeType; never hardcodes one).

import type { EdgeType } from '@/types/value-graph';
import type { NodeMetadata } from '@/services/canvas-mappers';

export interface ConnectableNode {
  type?: string;
  metadata?: NodeMetadata;
}

const NEED_TYPES = new Set(['job', 'challenge', 'opportunity']);

function isSynthesised(node: ConnectableNode): boolean {
  return node.metadata?.source === 'synthesis';
}

// Evidence overlay (market_signal / assumption) attaches to the backend's
// overlay targets (synthesised needs + capabilities, per shared/overlay-model)
// PLUS the idea — the backend helper deliberately excludes the idea because it
// treats it as each consumer's fallback, but idea-attached evidence is real in
// the data model, so a manual edge onto it is legitimate. Never raw needs
// (they hide behind their synthesised parent) and never other overlay nodes.
function isOverlayTarget(node: ConnectableNode): boolean {
  if (node.type === 'capability' || node.type === 'idea') return true;
  return NEED_TYPES.has(node.type ?? '') && isSynthesised(node);
}

/**
 * The relationship a manual source → target connection expresses, or null when
 * the pair isn't valid. Mirrors the invariants the backend already relies on:
 * capabilities address *synthesised* challenges/opportunities only (raw ones —
 * including manually added — go through synthesis first), and jobs are reached
 * transitively via improves/impedes, never from a capability directly.
 * `depends-on` / `blocks` have no manual path — nothing generates them today.
 */
export function inferEdgeType(source: ConnectableNode, target: ConnectableNode): EdgeType | null {
  const s = source.type ?? '';
  const t = target.type ?? '';

  if (s === 'challenge' && t === 'job') return 'impedes';
  if (s === 'opportunity' && t === 'job') return 'improves';

  if (s === 'capability' && t === 'challenge' && isSynthesised(target)) return 'mitigates';
  if (s === 'capability' && t === 'opportunity' && isSynthesised(target)) return 'enables';

  if (s === 'market_signal' && isOverlayTarget(target)) return 'evidences';
  if (s === 'assumption' && isOverlayTarget(target)) return 'assumption-node';

  // The PM asserts which capabilities the idea intended; step 7 reads this.
  if (s === 'idea' && t === 'capability') return 'comprises';

  return null;
}
